/**
 * HOUR OF DAY — all 24 buckets, zero-filled by the server.
 *
 * ONE COLOUR AND NO LEGEND, and that is the correct answer rather than a
 * shortcut. This is a single series: the panel title names it, and a legend of
 * one entry explains nothing. The sequential ramp would be right if these were
 * heatmap CELLS, where the colour has to carry the magnitude — as bars the
 * height already does, and painting the height a second time in colour is
 * redundancy that reads as meaning.
 *
 * `hour_of_day` IS zero-filled, so a dead hour comes back as a real zero and
 * this chart shows 24 bars every time. That is the whole value of the panel:
 * 04:00 being empty is a fact about the building, and a chart that silently
 * skipped it would show a 20-hour day.
 *
 * ALL 24 BARS, EVERY OTHER LABEL. `interval={0}` was tried first on the
 * analytics screen and the first browser pass rendered the axis as
 * "00:001:002:003:00" — 24 labels of about 32px in 640px of track, colliding
 * into a smear. Dropping BARS to fit the labels would hide the empty hours this
 * panel exists to show, so the LABELS thin out instead.
 */
import React from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { AnalyticsBatchOk, AnalyticsBatchResult } from '../../../../../api/analytics-client';
import { dataViz } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { DashSimpleTooltip } from '../charts/chart-tooltip';
import {
  BAR_RADIUS,
  cursor,
  format,
  grid,
  margins,
  markSeparator,
  xAxisCategory,
  yAxisCount,
  firstPaintAnimation,
} from '../charts/chart-theme';
import { readSlot, rowsFromItems, slotState, substitutionCaveat } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';

const PANEL_ID = 'hour';

interface Bucket {
  key: string;
  label: string;
  value: number;
}

export const bucketsFrom = (data: AnalyticsBatchOk): Bucket[] =>
  [...data.items]
    .map(item => ({
      key: item.key,
      label: item.label,
      value: format.numberOf(item.value) ?? 0,
    }))
    // Chronological. The server already returns the chronological dimensions in
    // order; the sort is here so a future limit or a re-order upstream cannot
    // turn the day inside out.
    .sort((a, b) => a.key.localeCompare(b.key));

export interface HourPanelProps {
  state: ChartShellState;
  result?: AnalyticsBatchResult;
  drill: DrillHandlers;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const HourPanel: React.FC<HourPanelProps> = ({
  state,
  result,
  drill,
  onExportPng,
  onExportCsv,
}) => {
  const slot = readSlot(result);
  const data = slot.ok;
  const buckets = data ? bucketsFrom(data) : [];
  const peak = buckets.reduce(
    (best, bucket) => (bucket.value > best.value ? bucket : best),
    buckets[0] || { key: '', label: '', value: 0 }
  );
  const noun = data?.noun || ['record', 'records'];

  return (
    <ChartShell
      id={PANEL_ID}
      title="Hour of day"
      /* FULL WIDTH. 24 buckets in a half-width column is 500px of track for a
         day, which forces the panel to scroll on a desktop where there is room
         for it. Wide, the whole day fits at lg+ and only a phone has to scroll. */
      wide
      aspect={3.2}
      minHeight={240}
      minWidth={640}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat="Every hour of the day, including the empty ones. An hour is the local hour of the building the record belongs to, not the reader's."
      extraCaveat={substitutionCaveat(data)}
      emptyReason={
        slot.empty
          ? {
              title: 'No hours to plot',
              text: 'Nothing was recorded in this period, so there is no shape to the day yet.',
            }
          : null
      }
      summary={
        buckets.length
          ? `${data?.label || 'Records'} by hour of the day. Busiest hour ${
              peak.label
            } with ${peak.value.toLocaleString()}.`
          : undefined
      }
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      table={
        data ? (
          <ChartTable
            dimensionLabel="Hour"
            valueLabel={data.label}
            rows={rowsFromItems(data)}
            showCompare={data.compareTotal != null}
          />
        ) : undefined
      }
    >
      <BarChart accessibilityLayer data={buckets} margin={margins.default} barCategoryGap="18%">
        <CartesianGrid {...grid} />
        <XAxis dataKey="label" {...xAxisCategory} interval={1} />
        <YAxis {...yAxisCount} />
        <Tooltip
          content={
            <DashSimpleTooltip
              tone={dataViz.series[0]}
              valueKey="value"
              labelKey="label"
              noun={noun}
              note={row =>
                row.label === peak.label ? 'The busiest hour in this period.' : null
              }
            />
          }
          cursor={cursor.bar}
        />
        <Bar
          dataKey="value"
          fill={dataViz.series[0]}
          radius={BAR_RADIUS}
          {...markSeparator}
          onClick={drill.onMark}
          cursor="pointer"
          {...firstPaintAnimation(PANEL_ID)}
        />
      </BarChart>
    </ChartShell>
  );
};
